/**
 * Notifications Center
 * Bell icon in navigation that opens a slide-out panel with:
 * - Aggregated activity from across the app
 * - Real-time task assignment notifications
 * - Read/unread status
 * - Mark as resolved functionality
 * - Filtering and search
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Bell, CheckCircle2, TrendingUp, AlertTriangle, Users, 
  Sparkles, Clock, Filter, MoreHorizontal, Check, Trash2,
  Eye, EyeOff, Settings, ChevronRight, Target, Calendar,
  DollarSign, MessageSquare, FileText, Star, Zap, ListTodo,
  UserPlus, ClipboardCheck
} from 'lucide-react';
import { toast } from '@/utils/safeToast';
import { useClient } from '@/contexts/ClientContext';
import { subscribeToTaskNotifications, getTasks, getActivityFeed, markActivitiesRead } from '@/services/staffPerformanceApi';
import { generateSystemNotifications, formatTimeAgo } from '@/utils/mockData';
import { ACTIVITY_TYPES } from './_activity_types';


export default function NotificationsCenter({ triggerClassName = '' }) {
  const navigate = useNavigate();
  const { customerBusiness } = useClient();
  const industry = customerBusiness?.industry?.toLowerCase() || 'hospitality';
  
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState(() => generateSystemNotifications(industry));
  const [filter, setFilter] = useState('all');
  const [taskNotifications, setTaskNotifications] = useState([]);
  
  // Subscribe to real-time task notifications
  useEffect(() => {
    const unsubscribe = subscribeToTaskNotifications((tasks, action) => {
      if (action === 'new_tasks' || action === 'created') {
        const newNotifications = tasks.map(task => ({
          id: `task_${task.task_id}_${Date.now()}`,
          type: 'task_assigned',
          title: 'New Task Assigned',
          message: `${task.title} - Priority: ${task.priority}`,
          time: 'Just now',
          read: false,
          priority: task.priority === 'critical' ? 'high' : task.priority === 'high' ? 'high' : 'medium',
          route: '/app/staff-performance',
          taskData: task
        }));
        
        setNotifications(prev => [...newNotifications, ...prev]);
        
        // Show toast for new task
        tasks.forEach(task => {
          toast.info(`New task: ${task.title}`, {
            description: `Priority: ${task.priority}`,
            action: {
              label: 'View',
              onClick: () => navigate('/app/staff-performance')
            }
          });
        });
      }
    });
    
    // Fetch activity feed from backend
    const fetchActivityFeed = async () => {
      try {
        const data = await getActivityFeed({ limit: 20 });
        if (data.success && data.activities && data.activities.length > 0) {
          const activityNotifications = data.activities.map(activity => ({
            id: activity.activity_id,
            type: activity.type,
            title: activity.title,
            message: activity.message,
            time: formatTimeAgo(activity.timestamp),
            read: activity.read,
            priority: activity.priority || 'medium',
            route: activity.route || '/app/staff-performance',
            data: activity.data
          }));
          setTaskNotifications(activityNotifications);
        }
      } catch (error) {
        // Fallback to fetching just tasks
        try {
          const data = await getTasks({ limit: 5 });
          if (data.tasks && data.tasks.length > 0) {
            const recentTaskNotifications = data.tasks.slice(0, 3).map(task => ({
              id: `task_${task.task_id}`,
              type: 'task_assigned',
              title: task.status === 'completed' ? 'Task Completed' : 'Task Update',
              message: `${task.title}`,
              time: formatTimeAgo(task.created_at),
              read: task.status === 'completed',
              priority: task.priority === 'critical' || task.priority === 'high' ? 'high' : 'medium',
              route: '/app/staff-performance',
              taskData: task
            }));
            setTaskNotifications(recentTaskNotifications);
          }
        } catch (taskError) {
          // Activity feed not available, using fallback
        }
      }
    };
    
    fetchActivityFeed();
    
    // Poll for updates every 30 seconds
    const pollInterval = setInterval(fetchActivityFeed, 30000);
    
    return () => {
      unsubscribe();
      clearInterval(pollInterval);
    };
  }, [navigate]);
  
  // Merge task notifications with base notifications
  const allNotifications = useMemo(() => {
    const combined = [...taskNotifications, ...notifications];
    // Remove duplicates by id
    const unique = combined.filter((n, i, arr) => arr.findIndex(x => x.id === n.id) === i);
    return unique;
  }, [notifications, taskNotifications]);
  
  // Counts
  const unreadCount = allNotifications.filter(n => !n.read).length;
  const highPriorityCount = allNotifications.filter(n => n.priority === 'high' && !n.read).length;
  
  // Filtered notifications
  const filteredNotifications = useMemo(() => {
    let filtered = [...allNotifications];
    
    if (filter === 'unread') {
      filtered = filtered.filter(n => !n.read);
    } else if (filter !== 'all') {
      filtered = filtered.filter(n => n.type === filter);
    }
    
    return filtered;
  }, [allNotifications, filter]);
  
  // Mark as read
  const markAsRead = (id) => {
    setNotifications(prev => prev.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };
  
  // Mark all as read
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    toast.success('All notifications marked as read');
  };
  
  // Delete notification
  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    toast.success('Notification removed');
  };
  
  // Handle notification click
  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setIsOpen(false);
    if (notification.route) {
      navigate(notification.route);
    }
  };
  
  // Get icon component for notification type
  const getTypeIcon = (type) => {
    const config = ACTIVITY_TYPES[type] || ACTIVITY_TYPES.alert;
    const Icon = config.icon;
    return <Icon className={`w-4 h-4 ${config.textColor}`} />;
  };
  
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`relative ${triggerClassName}`}
          data-testid="notifications-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-md p-0" data-testid="notifications-panel">
        <SheetHeader className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SheetTitle className="text-lg">Notifications</SheetTitle>
              {unreadCount > 0 && (
                <Badge variant="secondary" className="bg-red-100 text-red-600">
                  {unreadCount} new
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                  <Check className="w-4 h-4 mr-1" />
                  Mark all read
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Settings className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>Notification Settings</DropdownMenuItem>
                  <DropdownMenuItem className="text-red-600">Clear All</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <SheetDescription className="text-xs">
            {highPriorityCount > 0 && (
              <span className="text-red-500 font-medium">{highPriorityCount} high priority • </span>
            )}
            Stay updated with your business activity
          </SheetDescription>
        </SheetHeader>
        
        {/* Filter Tabs */}
        <div className="border-b">
          <Tabs value={filter} onValueChange={setFilter} className="w-full">
            <TabsList className="w-full justify-start rounded-none bg-transparent border-0 p-0 h-auto">
              <TabsTrigger 
                value="all" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
              >
                All
              </TabsTrigger>
              <TabsTrigger 
                value="unread"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
              >
                Unread
                {unreadCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 bg-red-100 text-red-600">
                    {unreadCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="alert"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
              >
                Alerts
              </TabsTrigger>
              <TabsTrigger 
                value="insight"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
              >
                AI
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        {/* Notifications List */}
        <ScrollArea className="h-[calc(100vh-180px)]">
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bell className="w-12 h-12 mb-3 opacity-20" />
              <p className="text-sm">No notifications</p>
              <p className="text-xs">You&apos;re all caught up!</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredNotifications.map(notification => {
                const typeConfig = ACTIVITY_TYPES[notification.type] || ACTIVITY_TYPES.alert;
                
                return (
                  <div 
                    key={notification.id}
                    className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors ${!notification.read ? 'bg-primary/5' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className={`p-2 rounded-lg ${typeConfig.bgColor} flex-shrink-0`}>
                        {getTypeIcon(notification.type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-medium ${!notification.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {notification.title}
                              </p>
                              {notification.priority === 'high' && !notification.read && (
                                <Badge variant="destructive" className="h-4 px-1 text-[10px]">
                                  High
                                </Badge>
                              )}
                              {!notification.read && (
                                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                          </div>
                          
                          {/* Actions */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {!notification.read && (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Mark as read
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id); }} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        {/* Time */}
                        <div className="flex items-center gap-2 mt-2">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">{notification.time}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t bg-background">
          <Button variant="outline" className="w-full" onClick={() => { setIsOpen(false); navigate('/app'); }}>
            View All Activity
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
